import * as THREE from 'three'
import { themes, themeOrder } from '../../content/palette.js'
import { lineTexture } from '../Utils/localizedTexture.js'
import { t, pick } from '../../content/i18n.js'

/**
 * Renk dünyaları: T tuşu ya da TEMA alanı (Enter / dokunma) sıradaki temaya geçer.
 * Zemin, dolaylı ışık, gölgeler, bake edilmiş zemin gölgeleri, ekran parlaması ve sayfa arka planı birlikte değişir.
 */
export default class Themes
{
    constructor(_options)
    {
        // Options
        this.config = _options.config
        this.floor = _options.floor
        this.materials = _options.materials
        this.shadows = _options.shadows
        this.objects = _options.objects
        this.passes = _options.passes
        this.areas = _options.areas
        this.sounds = _options.sounds
        this.x = _options.x
        this.y = _options.y

        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false
        this.listeners = new Set()

        this.setArea()
        this.setKey()
        this.apply(this.config.theme)
    }

    labelText()
    {
        return `${t('floorTheme')}   ${pick(themes[this.current].name)}`
    }

    apply(_name)
    {
        const theme = themes[_name] || themes[themeOrder[0]]
        this.current = themes[_name] ? _name : themeOrder[0]
        this.config.theme = this.current

        // Zemin gradyanı
        Object.assign(this.floor.colors, theme.floor)
        this.floor.updateMaterial()

        // Nesnelere yansıyan dolaylı ışık — yalnız bu uniform: shades.updateMaterials() açılış
        // (uRevealProgress) değerini de 0'a çekip bütün nesneleri görünmez yapıyordu
        this.materials.shades.indirectColor = theme.indirectColor
        this.materials.shades.uniforms.uIndirectColor = new THREE.Color(theme.indirectColor)
        for(const _key in this.materials.shades.items)
        {
            this.materials.shades.items[_key].uniforms.uIndirectColor.value = this.materials.shades.uniforms.uIndirectColor
        }

        // Hareketli nesne gölgeleri
        this.shadows.color = theme.shadowColor
        this.shadows.materials.base.uniforms.uColor.value = new THREE.Color(theme.shadowColor)
        for(const _shadow of this.shadows.items)
        {
            _shadow.material.uniforms.uColor.value = new THREE.Color(theme.shadowColor)
        }

        // Bake edilmiş zemin gölgeleri (Materials.floorShadow.updateMaterials objects'e erişemiyor, doğrudan güncellenir)
        this.materials.items.floorShadow.shadowColor = theme.shadowColor
        for(const _mesh of this.objects.floorShadows)
        {
            _mesh.material.uniforms.uShadowColor.value = new THREE.Color(theme.shadowColor)
        }

        // Ekran parlaması
        const glow = new THREE.Color(theme.glow)
        glow.convertLinearToSRGB()
        this.passes.glowsPass.color = theme.glow
        this.passes.glowsPass.material.uniforms.uColor.value = glow

        // Sayfa arka planı (yüklenirken ve kenarlarda görünür)
        document.documentElement.style.background = theme.background
        document.body.style.background = theme.background
        document.documentElement.dataset.theme = _name // menünün cam rengi (main.css, html[data-theme])
        const meta = document.querySelector('meta[name="theme-color"]')
        if(meta)
        {
            meta.setAttribute('content', theme.background)
        }

        if(this.label)
        {
            this.label.texture.userData.entry.render()
        }
    }

    next()
    {
        const index = themeOrder.indexOf(this.current)
        this.select(themeOrder[(index + 1) % themeOrder.length])
    }

    // Menü ve T tuşu: uygula + hatırla + ses
    select(_name)
    {
        this.apply(_name)
        this.sounds.play('uiArea')
        for(const _listener of this.listeners)
        {
            _listener(this.current)
        }

        try
        {
            window.localStorage.setItem('llpause.theme', this.current)
        }
        catch(_error) {}
    }

    setKey()
    {
        window.addEventListener('keydown', (_event) =>
        {
            if(_event.code === 'KeyT' && !_event.ctrlKey && !_event.metaKey && !_event.altKey)
            {
                this.next()
            }
        })
    }

    setArea()
    {
        this.current = this.config.theme

        this.area = this.areas.add({
            position: new THREE.Vector2(this.x, this.y),
            halfExtents: new THREE.Vector2(1.5, 1.5)
        })
        this.area.on('interact', () =>
        {
            this.next()
        })

        // Alanın altında zemin yazısı: seçili tema
        this.label = {}
        this.label.texture = lineTexture(() => this.labelText(), 1024, 128)
        this.label.mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(5.2, 0.65),
            new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.label.texture })
        )
        this.label.mesh.position.set(this.x, this.y - 2.2, 0.002)
        this.label.mesh.matrixAutoUpdate = false
        this.label.mesh.updateMatrix()
        this.container.add(this.label.mesh)
    }
}
